// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState, useEffect, useMemo } from "react";

type Compound = {
  key: "urolithinA" | "luteolin" | "spermidine";
  name: string;
  subtitle: string;
  origin: string;
  category: string;
  benefitTitle: string;
  benefitBody: string;
  imageUrl: string;
  accent: string;
};

function CellCard({
  title,
  body,
  tag,
  icon,
  bgImage,
  tint,
}: {
  title: string;
  body: string;
  tag: string;
  icon: string;
  bgImage: string;
  tint: string;
}) {
  return (
    <div
      className="
        group relative aspect-square rounded-2xl
        border border-gray-200 overflow-hidden
        transition-all duration-300 hover:shadow-lg
      "
    >
      {/* 🖼 Background image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* 🧊 CENTERED UI SQUARE (DEFAULT ON → disappears on hover) */}
      <div
        className="
          absolute inset-0 z-10
          flex items-center justify-center
          transition-opacity duration-200
          opacity-100 group-hover:opacity-0
        "
      >
        <div
          className="
            relative aspect-square
            w-[78%] max-w-[260px]
            rounded-xl
            overflow-hidden
          "
        >
          {/* Tint */}
          <div className={`absolute inset-0 bg-gradient-to-br ${tint}`} />

          {/* Frost */}
          <div className="absolute inset-0 bg-white/35 backdrop-blur-md" />

          {/* Content */}
          <div className="relative h-full p-4 flex flex-col justify-between">
            <div>
              <div className="text-xl mb-2">{icon}</div>

              <h3 className="text-base font-semibold text-gray-900 leading-tight">
                {title}
              </h3>

              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                {body}
              </p>
            </div>

            <span className="inline-block text-xs font-medium text-gray-700 border border-gray-300 rounded-full px-3 py-1 bg-white/70 w-fit">
              {tag}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}



export function RevionyxCompoundsShowcase({ contained = true }: { contained?: boolean }) {
  const compounds: Compound[] = useMemo(
    () => [
      {
        key: "urolithinA",
        name: "Pomegranate-Derived Postbiotic Metabolite",
        subtitle: "Rich in Urolithin A",
        origin: "Derived from pomegranate polyphenols via microbial metabolism",
        category: "Rich in Urolithin A",
        benefitTitle: "Cellular energy & vitality support",
        benefitBody: "Supports healthy cellular energy processes and helps maintain mitochondrial quality.",
        imageUrl: "urolithina.png",
        accent: "from-[#ACD7DE]/20 via-[#ACD7DE]/10 to-transparent",
      },
      {
        key: "luteolin",
        name: "Plant Flavonoid",
        subtitle: "Rich in Luleolin",
        origin: "Botanical flavonoid found in many plant sources",
        category: "Rich in Luteolin",
        benefitTitle: "Oxidative balance & resilience",
        benefitBody: "Helps support the body’s natural antioxidant defenses and healthy inflammatory balance.",
        imageUrl: "luteolin.png",
        accent: "from-[#ACD7DE]/20 via-[#ACD7DE]/10 to-transparent",
      },
      {
        key: "spermidine",
        name: "Wheat-Germ Derived Polyamine",
        subtitle: "Rich in Spermidine",
        origin: "A naturally occurring molecule present in living cells",
        category: "Rich in Spermidine",
        benefitTitle: "Cell maintenance & Integrity pathways",
        benefitBody:
          "Supports normal cellular maintenance processes and helps support healthy aging at a cellular level.",
        imageUrl: "spermidine.png",
        accent: "from-emerald-200/20 via-emerald-100/10 to-transparent",
      },
    ],
    []
  );

  const [activeKey, setActiveKey] = useState<Compound["key"]>("urolithinA");
  const active = compounds.find((c) => c.key === activeKey) ?? compounds[0];

  return (
    <section className="bg-white border-b border-gray-100 py-7 md:py-9">
      <div className={contained ? "max-w-6xl mx-auto px-6" : "w-full"}>
        {/* Compact header */}
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex items-center rounded-full border border-[#C7A64C]/25 bg-[#FFFDF5] px-3 py-1 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7B6A3B]">
            Revionyx™ Core Actives
          </span>
          <span className="hidden sm:inline text-[12px] text-gray-400 tracking-wide">
            science-driven • nature-powered
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
          <h3 className="text-xl md:text-2xl font-bold text-[#121212] tracking-tight">
            Major Components, Designed to Work Together
          </h3>
          <p className="text-sm text-gray-600 md:max-w-[520px] leading-relaxed">
            Three core bioactives—built for consistency, safety, and daily ritual.
          </p>
        </div>

        {/* Compact layout */}
        <div className="md:hidden">
        <div className="text-[11px] text-gray-500 mb-3">Scroll to explore each active</div>

        {/* snap container */}
        <div className="space-y-4 snap-y snap-mandatory">
          {compounds.map((c) => (
            <section
              key={c.key}
              className="
                snap-start
                rounded-2xl border border-gray-200/70 bg-white
                shadow-[0_14px_48px_rgba(0,0,0,0.06)]
                overflow-hidden
              "
            >
              <div className={`h-2 w-full bg-gradient-to-r ${c.accent}`} />

              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                    <img
                      src={c.imageUrl}
                      alt={c.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-semibold">
                      Core Active
                    </div>
                    <div className="text-lg font-bold text-[#121212] tracking-tight">
                      {c.name}
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{c.subtitle}</div>
                  </div>
                </div>

                <div className="grid gap-3 mt-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                    <div className="text-xs font-semibold text-[#121212] mb-1">What it is</div>
                    <p className="text-xs text-gray-600 leading-relaxed">{c.origin}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                    <div className="text-xs font-semibold text-[#121212] mb-1">Why it matters</div>
                    <div className="text-xs font-bold text-[#121212]">{c.benefitTitle}</div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-1">{c.benefitBody}</p>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-[11px] leading-relaxed text-gray-500">
                    *Informational only. Consult your veterinarian for your pet’s needs.
                  </p>
                </div>
              </div>
            </section>
          ))}
        </div>
      </div>



      <div className="hidden md:grid md:grid-cols-[1fr_1.4fr] gap-6 items-stretch">
      {/* Left: compact vertical index */}

          <div className="rounded-2xl border border-gray-200/70 bg-white shadow-sm p-3">
            <div className="text-sm font-semibold text-[#121212] mb-2">Active Index</div>
            <div className="text-[11px] text-gray-500 mb-3">Hover or tap to explore</div>

            <div className="space-y-2">
              {compounds.map((c) => {
                const isActive = c.key === activeKey;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onMouseEnter={() => setActiveKey(c.key)}
                    onFocus={() => setActiveKey(c.key)}
                    onClick={() => setActiveKey(c.key)}
                    className={[
                      "w-full text-left px-3 py-2 rounded-lg border transition-all duration-200",
                      "hover:border-gray-300 hover:bg-gray-50",
                      isActive
                        ? "border-[#acd7de]/40 bg-[#FFFDF5] shadow-[0_6px_18px_rgba(199,166,76,0.12)]"
                        : "border-gray-200 bg-white",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={["h-2 w-2 rounded-full", isActive ? "bg-[#acd7de]" : "bg-gray-300"].join(" ")} />
                        <div className="text-sm font-semibold text-[#121212]">{c.name}</div>
                      </div>
                      <span className="text-gray-300">→</span>
                    </div>

                    <div className="mt-0.5 text-[11px] text-gray-600">{c.category}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-3 text-[11px] text-gray-500 leading-relaxed">
              *Informational only. Consult your veterinarian for your pet’s needs.
            </div>
          </div>

          {/* Right: compact details */}
          <div className="rounded-2xl border border-gray-200/70 bg-white shadow-[0_14px_48px_rgba(0,0,0,0.06)] overflow-hidden">
            {/* Subtle top tint */}
            <div className={`h-2 w-full bg-gradient-to-r ${active.accent}`} />

            <div className="p-4 md:p-5">
              <div className="flex items-start gap-4">
                {/* Small image (no tall hero banner) */}
                <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                  <img src={active.imageUrl} alt={active.name} className="w-full h-full object-cover" loading="lazy" />
                </div>

                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-semibold">Core Active</div>
                  <div className="text-lg md:text-xl font-bold text-[#121212] tracking-tight">{active.name}</div>
                  <div className="text-sm text-gray-700 mt-0.5">{active.subtitle}</div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                  <div className="text-xs font-semibold text-[#121212] mb-1">What it is</div>
                  <p className="text-xs text-gray-600 leading-relaxed">{active.origin}</p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                  <div className="text-xs font-semibold text-[#121212] mb-1">Why it matters</div>
                  <div className="text-xs font-bold text-[#121212]">{active.benefitTitle}</div>
                  <p className="text-xs text-gray-600 leading-relaxed mt-1">{active.benefitBody}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


const SciencePage: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // lock body scroll when menu is open
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const goTo = (href: string) => {
    setMobileOpen(false);

    // internal anchor on the same page
    if (href.startsWith("#")) {
      requestAnimationFrame(() => {
        const el = document.querySelector(href);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }

    // normal navigation
    window.location.href = href;
  };


  const [email, setEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
  const scrollToHash = () => {
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // first load
  const t = setTimeout(scrollToHash, 50);

  // if hash changes while on the page
  window.addEventListener("hashchange", scrollToHash);

  return () => {
    clearTimeout(t);
    window.removeEventListener("hashchange", scrollToHash);
  };
  }, []);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Email submitted:", email);
  };

  return (
<div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
{/* Navigation */}
<nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/98 backdrop-blur-md shadow-sm' : 'bg-white'} border-b border-gray-100`}>
<div className="max-w-6xl mx-auto px-6 py-4">
<div className="flex items-center justify-between">
<a href="/" className="flex items-center">
  <img
    src="/logo.svg"
    alt="Together Ever Logo"
    className="h-5 md:h-6 w-auto"
  />
</a>
<div className="hidden md:flex items-center space-x-8">
<a href="/" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">Home</a>
<a href="/science" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">Science</a>
<a href="/about" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">About</a>
<button className="bg-[#121212] text-white px-6 py-1 text-sm font-medium rounded-button hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wide">
Join Waitlist
</button>
</div>

{/* Mobile controls */}
      <div className="flex items-center gap-3 md:hidden">
        {/* Hamburger */}
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          className="h-9 w-9 grid place-items-center rounded-lg hover:bg-gray-50 active:bg-gray-100"
        >
          <span className="block w-5">
            <span className={`block h-[2px] bg-gray-800 transition-all ${mobileOpen ? "translate-y-[6px] rotate-45" : ""}`} />
            <span className={`block h-[2px] bg-gray-800 mt-[5px] transition-all ${mobileOpen ? "opacity-0" : ""}`} />
            <span className={`block h-[2px] bg-gray-800 mt-[5px] transition-all ${mobileOpen ? "-translate-y-[8px] -rotate-45" : ""}`} />
          </span>
        </button>
      </div>
    </div>
  </div>

  {/* Mobile menu panel */}
  <div className={`md:hidden ${mobileOpen ? "block" : "hidden"}`}>
    {/* backdrop */}
    <div
      className="fixed inset-0 bg-black/20"
      onClick={() => setMobileOpen(false)}
    />

    {/* sheet */}
    <div
    className="
      fixed top-[64px] left-0 right-0
      rounded
      bg-white/55 backdrop-blur-xl backdrop-brightness-110 backdrop-saturate-150
      border border-white/40
      shadow-[0_18px_60px_rgba(0,0,0,0.16)]
      overflow-hidden
    "
  >
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/35 via-white/10 to-transparent" />
        <div className="relative px-4 py-4 space-y-2">
          <button
            onClick={() => goTo("/")}
            className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
          >
            Home
          </button>
          <button
            onClick={() => goTo("/science")}
            className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
          >
            Science
          </button>
          <button
            onClick={() => goTo("/about")}
            className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
          >
            About
          </button>
  </div>
  </div>
  </div>
  </nav>


      {/* ✅ SCIENCE HERO — NEW (simple, premium, not your home hero) */}
      <section className="bg-white border-b border-gray-100 pt-16 pb-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="inline-flex items-center rounded-full border border-[#C7A64C]/25 bg-[#FFFDF5] px-3 py-1 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7B6A3B]">
                  Science
                </span>
                <span className="inline-flex items-center rounded-full border border-[#acd7de]/35 bg-[#F4FBFC] px-3 py-1 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#2F5B63]">
                  Revionyx™ Complex
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-[#121212] leading-[1.08] tracking-tight">
                The Science Made Clear
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mt-4 max-w-2xl">
                Revionyx™ is designed to support normal healthy-aging processes in dogs & cats through a small set of
                carefully selected bioactives and quality-first standards.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="#model"
                  className="border-2 border-[#121212] text-[#121212] px-6 py-3 text-sm font-medium !rounded-button hover:bg-[#121212] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wide"
                >
                  See the model →
                </a>
                <a
                  href="#actives"
                  className="bg-[#121212] text-white px-6 py-3 text-sm font-medium !rounded-button hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wide"
                >
                  Explore ingredients
                </a>
              </div>
            </div>

            {/* Right: simple premium panel */}
            <div className="rounded-2xl border border-gray-200/70 bg-gradient-to-br from-[#FAF9F5] via-white to-[#ACD7DE]/10 shadow-[0_14px_48px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="h-2 w-full bg-gradient-to-r from-[#ACD7DE]/50 via-[#C7A64C]/25 to-transparent" />
              <div className="p-5">
                <div className="text-sm font-bold text-[#121212]">At a glance</div>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  A simple 3-pillar model: energy support, oxidative balance, and cellular maintenance—built for daily ritual.
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  {[
                    { t: "Energy", i: "⚡" },
                    { t: "Balance", i: "🛡️" },
                    { t: "Maintenance", i: "🔁" },
                  ].map((x) => (
                    <div key={x.t} className="rounded-xl border border-gray-200 bg-white p-3 text-center">
                      <div className="text-xl">{x.i}</div>
                      <div className="text-[12px] font-semibold text-gray-700 mt-2">{x.t}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 text-[12px] text-gray-500 leading-relaxed">
                  For animal use only. Consult your veterinarian for individual needs.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ MODEL — concise, premium (cell texture + frosted glass) */}
    <section id="model" className="py-12 bg-white border-b border-gray-100">
    <div className="max-w-6xl mx-auto px-6">
        <div className="mb-7">
        <h2 className="text-3xl md:text-3xl font-bold text-[#121212] leading-tight tracking-tight">
            A Three-Pilar Science Model
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mt-3 max-w-3xl">
            We focus on supporting healthy aging functions 
        </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
        {[
            {
            title: "Cellular Energy Support",
            body: "Supports healthy energy processes and helps maintain mitochondrial.",
            tag: "Urolithin A",
            icon: "⚡",
            bgImage: "/cells-energy.png",
            tint: "from-[#ACD7DE]/20 via-white/10 to-transparent",
            },
            {
            title: "Oxidative Balance",
            body: "Helps support natural antioxidant defenses and healthy inflammatory balance.",
            tag: "Luteolin",
            icon: "🛡️",
            bgImage: "/cells-balance.png",
            tint: "from-[#C7A64C]/18 via-white/10 to-transparent",
            },
            {
            title: "Maintenance & Integrity",
            body: "Supports normal cellular maintenance processes for long-horizon aging support.",
            tag: "Spermidine",
            icon: "🔁",
            bgImage: "/cells-maintainance.png",
            tint: "from-emerald-200/18 via-white/10 to-transparent",
            },
        ].map((x) => (
            <CellCard
            key={x.title}
            title={x.title}
            body={x.body}
            tag={x.tag}
            icon={x.icon}
            bgImage={x.bgImage}
            tint={x.tint}
            />
        ))}
        </div>
                <div className="mt-6 text-center text-[12px] text-gray-500 leading-relaxed">
            We focus on wellness support and quality standards. Additional scientific context may be shared over time.
        </div>
    </div>
    </section>


      {/* ✅ CORE ACTIVES — your component, framed cleanly */}
      <div id="actives" className="bg-white">
        <RevionyxCompoundsShowcase contained={true} />
      </div>

      {/* ✅ SAFETY + QUALITY — short */}
      <section id="safety" className="py-12 bg-[#F8F8F8] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#121212] mb-4 leading-tight tracking-tight">
              Safety & Quality
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Designed with the best standard for your dogs & cats.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
              <h4 className="text-lg font-bold text-[#121212] mb-3">Safety guidance</h4>
              <ul className="space-y-2 text-gray-600 leading-relaxed">
                <li>• Intended for healthy adult pets (1 year+).</li>
                <li>• Consult your veterinarian if pregnant/nursing, on medication, or managing a condition.</li>
                <li>• For sensitive stomachs, start smaller and increase gradually.</li>
                <li>• Discontinue use if adverse reactions occur.</li>
              </ul>
            </div>

            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
              <h4 className="text-lg font-bold text-[#121212] mb-3">Quality standards</h4>
              <ul className="space-y-2 text-gray-600 leading-relaxed">
                <li>• Defined specifications for ingredient identity and purity.</li>
                <li>• Documented manufacturing controls to support batch-to-batch consistency.</li>
                <li>• Stability and storage guidelines to help maintain product integrity.</li>
                <li>• Manufacturing and testing practices aligned with applicable industry standards</li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* Join Movement */}
      <section className="py-12 bg-gradient-to-r from-[#acd7de] to-[#acd7de]">
      <div className="max-w-4xl mx-auto px-6 text-center">
      <h2
      className="text-4xl font-bold text-white mb-6"
      style={{ fontFamily: 'Playfair Display, serif' }}
      >
      Join the Longevity Movement
      </h2>
      <p className="text-lg text-white opacity-90 mb-8 max-w-2xl mx-auto">
      Be among the first to give your pet the gift of extended, healthy years. Join thousands of pet families already on this journey.
      </p>
      <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
      <input
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="Enter your email"
      className="flex-1 px-4 py-3 rounded-lg border-none text-[#121212] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white text-sm"
      required
      />
      <button
      type="submit"
      className="bg-[#121212] text-white px-6 py-3 !rounded-button hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
      >
      Get Early Access
      </button>
      </div>
      </form>
      <p className="text-white opacity-75 text-sm mt-4">
      No spam, just longevity insights and early access to Together Ever™
      </p>
      </div>
      </section>

      {/* ✅ Footer — EXACT COPY FROM MAIN */}
      <footer className="py-16 bg-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="space-y-4">
              <a href="/" className="inline-block">
                <img
                  src="/logo.svg"
                  alt="Together Ever"
                  className="h-8 w-auto"
                />
              </a>

              <p className="text-[#121212] opacity-70 leading-relaxed max-w-xs">
                Support healthy aging in pets through longevity science
              </p>

              <p className="text-[#121212] opacity-70">
                support@togetherever.com
              </p>
            </div>

            <div></div>

            <div>
            <h4 className="font-bold text-[#121212] mb-4">Product</h4>
            <ul className="space-y-2 text-[#121212] opacity-70">
            <li><a href="/#hero" className="hover:text-[#C7A64C] transition-colors cursor-pointer">How It Works</a></li>
            <li><a href="/#revionyx" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Ingredients / Revionyx™</a></li>
            <li><a href="/#usage" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Usage Guide</a></li>
            <li><a href="/science#safety" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Safety & Quality</a></li>
            </ul>
            </div>

            <div>
            <h4 className="font-bold text-[#121212] mb-4">Venture</h4>
            <ul className="space-y-2 text-[#121212] opacity-70">
            <li><a href="/about#value" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Value</a></li>
            <li><a href="/about#team" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Team</a></li>
            <li><a href="/science" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Science</a></li>
            </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-[#121212] opacity-70 text-sm mb-4 md:mb-0">© 2025 Together Ever™. All rights reserved.</p>
              <p className="text-[#121212] opacity-70 text-xs max-w-md text-center md:text-right">
                *These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure,
                or prevent any disease.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* ✅ Keep your same global styles block */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Inter:wght@400;500;600;700&display=swap');

        /* Existing utilities */
        .!rounded-button {
          border-radius: 8px;
        }
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }

        /* 🌎 Global scaling adjustments */
        :root {
          --section-padding: 6rem;
          --heading-size: 2.25rem;
          --subheading-size: 1.125rem;
          --paragraph-size: 0.975rem;
        }

        /* Apply tighter section spacing */
        section {
          padding-top: var(--section-padding);
          padding-bottom: var(--section-padding);
        }

        /* Typography scale down for better 100% zoom balance */
        h1 {
          font-size: clamp(2.5rem, 4vw, 3.25rem);
          line-height: 1.15;
        }
        h2 {
          font-size: clamp(1.875rem, 3vw, 2.5rem);
          line-height: 1.2;
        }
        p, li {
          font-size: var(--paragraph-size);
          line-height: 1.65;
        }
      `}</style>
    </div>
  );
};

export default SciencePage;